import { Button, Skeleton, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ElementItemProps } from "../types";
import ElementFrame from "./ElementFrame";
import { useDevice } from "../../../../utils/DeviceProvider";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import { useIntl } from "react-intl";
import { useLocale } from "../../../../locales/LocaleProvider";
import { getLabel } from "../../../../utils/utils";
import { useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";
import FileOpenIcon from "@mui/icons-material/FileOpen";

const Txtarea = ({ label: labelParam, configKey, elementKey, disabled }: ElementItemProps<{}>) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [value, setValue] = useState<string | null>(null);

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const editedStyle = useEditedStyles();
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const { openContextMenu } = useResetContextMenu();

  const label = getLabel(labelParam, locale);

  const onChange = useCallback(() => {
    if (configIndex === null) {
      return;
    }
    currentValue[configKey].items[configIndex].value = value;
    setCurrentValue(currentValue);
  }, [setCurrentValue, currentValue, configKey, configIndex, value]);

  const importFile = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        const view = e.target === null || typeof e.target === "string" ? null : new Uint8Array(e.target.result as ArrayBuffer);
        const data = view ? new TextDecoder().decode(view).replace(/;/g, ",").replace(/\r/g, "") : e.target;
        if (data && typeof data === "string") {
          setValue(data);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }, []);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type, key }: { type: string; key?: string }) => type === "TXTAREA" && (key === elementKey || `${key}` === elementKey));
    setConfigIndex(cIndex);
  }, [configKey, currentValue, elementKey]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex]?.value ?? null;
    setSaved(svd);
    const current = currentValue[configKey].items[configIndex]?.value;
    setValue(current);
  }, [active, currentValue, config, configIndex, configKey]);

  if (value === null) {
    return (
      <ElementFrame label={label}>
        <Skeleton variant="rectangular" height={120} sx={{ width: "100%", borderRadius: 1 }} />
      </ElementFrame>
    );
  }

  if (configIndex === -1) {
    return (
      <ElementFrame label={label}>
        <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>
      </ElementFrame>
    );
  }

  return (
    <ElementFrame label={label} disabled={disabled}>
      <TextField
        hiddenLabel
        variant="filled"
        value={value}
        className={saved !== value ? editedStyle.input : ""}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onChange}
        sx={{ margin: 0, minWidth: 100, ".MuiInputBase-root": { paddingY: 0 } }}
        size="small"
        disabled={disabled}
        fullWidth
        multiline
        minRows={3}
        maxRows={10}
        autoComplete="off"
        onContextMenu={saved !== value ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value`) : undefined}
      />
      <Button component="label" startIcon={<FileOpenIcon />}>
        {formatMessage({ defaultMessage: "Import file" })}
        <input type="file" hidden value="" onChange={importFile} />
      </Button>
    </ElementFrame>
  );
};

export default Txtarea;
